'use client';
import { ReactNode } from 'react';
import { AppBar, Box, Divider, Drawer, IconButton, Toolbar } from '@mui/material';
import type { ModalProps } from '@mui/material/Modal';
import MenuIcon from '@mui/icons-material/Menu';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import { TextColor, BackgroundColor } from '../../../types/Color';

export interface HeaderV2Props {
  logo?: ReactNode;
  textColor?: TextColor | string;
  backgroundColor?: BackgroundColor | string;
  position?: 'absolute' | 'fixed' | 'relative' | 'static' | 'sticky';
  menu?: ReactNode;
  menuIconColor?:
    | 'disabled'
    | 'action'
    | 'inherit'
    | 'primary'
    | 'secondary'
    | 'error'
    | 'info'
    | 'success'
    | 'warning';
  drawer?: boolean;
  anchor?: 'left' | 'right' | 'top' | 'bottom';
  logoMobilePosition?: 'start' | 'center' | 'end';
  openMenu?: boolean;
  setOpenMenu?: (isOpen: boolean) => void;
  customMenuIcon?: ReactNode;
  customMenuCloseIcon?: ReactNode;
  divider?: boolean;
  shadow?: boolean;
  children?: ReactNode;
  /** Passed to the mobile Drawer’s underlying Modal (e.g. `keepMounted` so menu links stay in the DOM when closed). */
  drawerModalProps?: Partial<ModalProps>;
}

export function HeaderV2({
  logo,
  menu,
  textColor = TextColor.black,
  backgroundColor = BackgroundColor.transparent,
  position = 'static',
  menuIconColor = 'inherit',
  drawer = false,
  anchor = 'left',
  logoMobilePosition = 'center',
  openMenu = false,
  setOpenMenu,
  customMenuIcon,
  customMenuCloseIcon,
  divider = false,
  shadow = true,
  children,
  drawerModalProps,
}: HeaderV2Props) {
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
  });

  return (
    <Box component="header" sx={{ display: 'inline' }} data-testid="header-v2">
      <AppBar
        sx={{
          color: textColor,
          bgcolor: backgroundColor,
          p: 2,
          px: { md: 6, lg: 9, xl: 12 },
          boxShadow: shadow && trigger ? '0px 6px 12px rgba(0, 0, 0, 0.3)' : 'none',
        }}
        position={position}
      >
        <Toolbar disableGutters>
          {logo && (
            <Box
              sx={{
                flexGrow: 0,
                position:
                  anchor === 'right'
                    ? 'relative'
                    : drawer
                      ? 'absolute'
                      : { xs: 'absolute', md: 'relative' },
                mx: logoMobilePosition === 'start' ? 0 : 'auto',
                left: drawer ? 60 : 0,

                textAlign: anchor === 'right' || drawer ? 'left' : { xs: 'center', md: 'left' },
              }}
            >
              {logo}
            </Box>
          )}
          {menu && setOpenMenu && (
            <Box
              sx={{
                flexGrow: 1,
                display: drawer ? 'flex' : { xs: 'flex', md: 'none' },
                flexDirection: 'column',
                alignItems: anchor === 'right' ? 'end' : 'start',
                position: anchor === 'right' ? 'absolute' : 'unset',
                right: 0,
                marginRight: anchor === 'right' ? '-16px' : 0,
              }}
            >
              <IconButton
                size="large"
                aria-label="menu-button"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={() => setOpenMenu(!openMenu)}
                data-testid="toggle-menu-button"
                color="inherit"
              >
                {openMenu && customMenuCloseIcon
                  ? customMenuCloseIcon
                  : customMenuIcon || <MenuIcon color={menuIconColor} />}
              </IconButton>
              <Drawer
                anchor={anchor}
                open={openMenu}
                onClose={() => setOpenMenu(false)}
                ModalProps={drawerModalProps}
                sx={{
                  display: drawer ? 'flex' : { xs: 'block', md: 'none' },
                  // backgroundColor: backgroundColor,
                }}
              >
                {menu}
              </Drawer>
            </Box>
          )}
          {!drawer && menu && (
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'block' } }}>{menu}</Box>
          )}
          {children}
          {divider && !trigger && <Divider sx={{ mt: { xs: 11, sm: 12 } }} />}
        </Toolbar>
      </AppBar>
    </Box>
  );
}

export default HeaderV2;
